/**
 * AuditLog - Audit-Log Ansicht (Enterprise)
 * 
 * Features:
 * - Server-side Pagination
 * - Filter: Action, Entity, Zeitraum (Presets), Volltext
 * - Expandable Details mit JSON-View
 * - Layout wie Admin-Seiten (max-w-5xl)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import {
  Search,
  Loader2,
  AlertTriangle,
  Clock,
  User,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Filter,
  X,
  Shield,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';

interface AuditLogEntry {
  id: string;
  userId: string;
  action: string;
  entity: string;
  entityId: string | null;
  oldValue: string | null;
  newValue: string | null;
  details: string | null;
  ip: string | null;
  userAgent: string | null;
  timestamp: string;
  user: { id: string; displayName: string };
}

// Time range presets
const TIME_PRESETS = [
  { label: 'Letzte 24h', value: '24h', hours: 24 },
  { label: 'Letzte 7 Tage', value: '7d', hours: 168 },
  { label: 'Letzte 30 Tage', value: '30d', hours: 720 },
  { label: 'Alle', value: 'all', hours: 0 },
];

// Page size options
const PAGE_SIZES = [25, 50, 100];

// Actions for filter
const ACTIONS = ['LOGIN', 'LOGOUT', 'CREATE', 'UPDATE', 'DELETE', 'EXPORT'];

export default function AuditLogPage() {
  const { user } = useAuth();

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [timePreset, setTimePreset] = useState('7d');

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Calculate time range
  const getTimeRange = useCallback(() => {
    const preset = TIME_PRESETS.find(p => p.value === timePreset);
    if (!preset || preset.hours === 0) return { from: undefined, to: undefined };
    const now = new Date();
    const from = new Date(now.getTime() - preset.hours * 60 * 60 * 1000);
    return { from: from.toISOString(), to: now.toISOString() };
  }, [timePreset]);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      if (actionFilter) params.set('action', actionFilter);
      if (entityFilter) params.set('entity', entityFilter);
      // Suche gehört in die Datenbankabfrage: ein Filter im Browser würde nur
      // die gerade geladene Seite durchsuchen und Treffer dahinter verschlucken.
      if (debouncedSearch) params.set('search', debouncedSearch);

      const { from, to } = getTimeRange();
      if (from) params.set('from', from);
      if (to) params.set('to', to);

      const res = await fetch(`/api/admin/audit?${params}`, {
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();

        setLogs(data.logs ?? []);
        setTotal(data.total ?? 0);
      } else if (res.status === 403) {
        setError('Keine Berechtigung für Audit-Logs');
      } else {
        setError('Fehler beim Laden');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, actionFilter, entityFilter, timePreset, debouncedSearch, getTimeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Action badge colors
  const getActionColor = (action: string) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-blue-100 text-blue-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      case 'LOGIN': return 'bg-purple-100 text-purple-800';
      case 'LOGOUT': return 'bg-slate-100 text-slate-800';
      case 'EXPORT': return 'bg-amber-100 text-amber-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  // Toggle row expansion
  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Copy to clipboard
  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Parse and format JSON safely
  const formatJson = (str: string | null) => {
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      return str;
    }
  };

  // Reset filters
  const resetFilters = () => {
    setActionFilter('');
    setEntityFilter('');
    setSearchText('');
    setTimePreset('7d');
    setPage(0);
  };

  // Has active filters?
  const hasFilters = actionFilter || entityFilter || searchText || timePreset !== '7d';

  // Pagination info
  const startIdx = page * pageSize + 1;
  const endIdx = Math.min((page + 1) * pageSize, total);
  const totalPages = Math.ceil(total / pageSize);

  // Access check: SUPERADMIN or SYSTEM only
  if (!user || !['SUPERADMIN', 'SYSTEM'].includes(user.role)) {
    return (
      <AdminLayout>
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg border p-12 text-center">
            <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900">Zugriff verweigert</h1>
            <p className="text-slate-500 mt-2">
              Audit-Logs sind nur für SYSTEM und SUPERADMIN zugänglich.
            </p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-6 h-6" />
              Audit-Log
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {total} Einträge {timePreset !== 'all' && `(${TIME_PRESETS.find(p => p.value === timePreset)?.label})`}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-slate-400" />
            
            {/* Action Filter */}
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Alle Aktionen</option>
              {ACTIONS.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>

            {/* Entity Filter */}
            <input
              type="text"
              placeholder="Entity..."
              value={entityFilter}
              onChange={(e) => { setEntityFilter(e.target.value); setPage(0); }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm w-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />

            {/* Time Preset */}
            <select
              value={timePreset}
              onChange={(e) => { setTimePreset(e.target.value); setPage(0); }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {TIME_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Suchen (User, Entity, Details)..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Reset */}
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                title="Filter zurücksetzen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
            <p className="text-slate-500 mt-2">Laden...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-slate-600">{error}</p>
            <button
              onClick={loadData}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Erneut versuchen
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-600">Keine Audit-Einträge gefunden</p>
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="mt-4 text-blue-600 hover:underline"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase w-8"></th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Zeitpunkt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Benutzer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Aktion</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Entity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase hidden lg:table-cell">ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {logs.map((log) => {
                    const isExpanded = expandedRows.has(log.id);
                    const hasDetails = log.details || log.oldValue || log.newValue;
                    
                    return (
                      <React.Fragment key={log.id}>
                        <tr 
                          className={`hover:bg-slate-50 ${hasDetails ? 'cursor-pointer' : ''}`}
                          onClick={() => hasDetails && toggleRow(log.id)}
                        >
                          <td className="px-4 py-3">
                            {hasDetails && (
                              isExpanded 
                                ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                : <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              {new Date(log.timestamp).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <span className="truncate max-w-[150px]">{log.user.displayName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getActionColor(log.action)}`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                            {log.entity}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 font-mono hidden lg:table-cell">
                            {log.entityId ? log.entityId.slice(0, 12) + '...' : '-'}
                          </td>
                        </tr>
                        
                        {/* Expanded Details Row */}
                        {isExpanded && hasDetails && (
                          <tr className="bg-slate-50">
                            <td colSpan={6} className="px-4 py-4">
                              <div className="space-y-3 text-sm">
                                {/* Details */}
                                {log.details && (
                                  <DetailBlock
                                    label="Details"
                                    data={formatJson(log.details)}
                                    rawText={log.details}
                                    onCopy={() => copyToClipboard(log.details!, `details-${log.id}`)}
                                    isCopied={copiedId === `details-${log.id}`}
                                  />
                                )}
                                
                                {/* Old Value */}
                                {log.oldValue && (
                                  <DetailBlock
                                    label="Alter Wert"
                                    data={formatJson(log.oldValue)}
                                    rawText={log.oldValue}
                                    onCopy={() => copyToClipboard(log.oldValue!, `old-${log.id}`)}
                                    isCopied={copiedId === `old-${log.id}`}
                                  />
                                )}
                                
                                {/* New Value */}
                                {log.newValue && (
                                  <DetailBlock
                                    label="Neuer Wert"
                                    data={formatJson(log.newValue)}
                                    rawText={log.newValue}
                                    onCopy={() => copyToClipboard(log.newValue!, `new-${log.id}`)}
                                    isCopied={copiedId === `new-${log.id}`}
                                  />
                                )}
                                
                                {/* Meta: IP + UserAgent */}
                                {(log.ip || log.userAgent) && (
                                  <div className="text-xs text-slate-500 pt-2 border-t">
                                    {log.ip && <span>IP: {log.ip}</span>}
                                    {log.ip && log.userAgent && <span className="mx-2">|</span>}
                                    {log.userAgent && <span className="truncate">UA: {log.userAgent.slice(0, 60)}...</span>}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Zeige</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  className="px-2 py-1 border border-slate-300 rounded text-sm"
                >
                  {PAGE_SIZES.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span>pro Seite</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  {startIdx}–{endIdx} von {total}
                </span>
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Zurück
                </button>
                <span className="text-sm text-slate-600">
                  Seite {page + 1} / {totalPages || 1}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page + 1 >= totalPages}
                  className="px-3 py-1 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Weiter
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

// Detail Block Component for expanded rows
function DetailBlock({ 
  label, 
  data, 
  rawText, 
  onCopy, 
  isCopied 
}: { 
  label: string; 
  data: any; 
  rawText: string;
  onCopy: () => void;
  isCopied: boolean;
}) {
  const isObject = typeof data === 'object' && data !== null;
  
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-500 uppercase">{label}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onCopy(); }}
          className="p-1 text-slate-400 hover:text-slate-600 rounded"
          title="Kopieren"
        >
          {isCopied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
        </button>
      </div>
      {isObject ? (
        <div className="bg-white border rounded p-3 font-mono text-xs overflow-x-auto">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="flex gap-2">
              <span className="text-slate-500">{key}:</span>
              <span className="text-slate-900">
                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border rounded p-3 font-mono text-xs text-slate-900 whitespace-pre-wrap break-all">
          {rawText}
        </div>
      )}
    </div>
  );
}
